/**
 * /demos — hub page listing every live demo scenario the platform ships.
 *
 * Right now there's just one (the Trust Certificate walkthrough) but this
 * scaffolds cleanly for future additions (e.g. Banker-Push demo, Scam Shield
 * demo, NAVI corpus training demo).
 */
import { Link } from "react-router-dom";
import {
  ShieldCheck, ArrowRight, Sparkles, Clock, Users, Zap,
} from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const DEMOS = [
  {
    id: "trust-certificate",
    to: "/demo/trust-certificate",
    eyebrow: "Cryptographic proof",
    title: "Trust Certificate — end-to-end walkthrough",
    body:
      "Watch an advisor at a fictional SA insurer send a real 20-year Term Life quote through the Altara pipeline. A cryptographically-signed Trust Certificate is minted; drop the file into the verify widget to see MATCH; drop a random file to see MISMATCH.",
    icon: ShieldCheck,
    duration: "≈ 3 min",
    audience: "Product · Compliance · Sales",
    highlights: [
      "One-click live scenario seed",
      "Real Ed25519 signature, real public JWKS",
      "7 pre-ordered walkthrough deep-links",
      "Downloadable demo quote PDF (95%-realistic)",
    ],
    status: "live",
  },
];

export default function DemosHubPage() {
  return (
    <div className="min-h-screen" style={{ background: "#F8F9FA" }}>
      <header className="text-white" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #061633 100%)` }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center font-display font-bold" style={{ background: GOLD, color: NAVY }}>A</div>
            <div className="leading-tight">
              <div className="text-[13px] font-semibold">Altara</div>
              <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Live demos</div>
            </div>
          </Link>
          <h1 className="font-display text-3xl lg:text-5xl font-semibold max-w-3xl">
            See Altara Consumer Intelligence, live.
          </h1>
          <p className="mt-4 text-sm lg:text-base text-white/80 max-w-2xl leading-relaxed">
            Every demo below runs against real production infrastructure. No mocks, no slideware — just click, seed a scenario, and walk your audience through cryptographically-verifiable, TCF-aligned consumer flows.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16 space-y-10" data-testid="demos-hub">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Available demos</div>
          <h2 className="font-display text-2xl lg:text-3xl font-semibold mt-1" style={{ color: NAVY }}>
            {DEMOS.length} live scenario{DEMOS.length !== 1 ? "s" : ""} · more coming
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {DEMOS.map((d) => <DemoCard key={d.id} demo={d} />)}
          <PlannedCard title="Banker-push flow"
            body="Live demo of a bank pushing a product-of-interest journey to a consumer via a signed magic link. Consumer accepts, discloses, records decision — full TCF trail." />
          <PlannedCard title="NAVI Scam Shield"
            body="Live demo of NAVI's grounded scam verdict, backed by a real Altara scam corpus with 👍/👎 feedback closing the loop." />
        </div>
      </main>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center text-[11px] text-slate-500">
          These demos run against live infrastructure. All institutions, consumers and advisors shown are fictional and used only for illustration.
        </div>
      </footer>
    </div>
  );
}

function DemoCard({ demo }) {
  const Icon = demo.icon;
  return (
    <Link
      to={demo.to}
      data-testid={`demo-card-${demo.id}`}
      className="group rounded-2xl bg-white border border-slate-100 shadow-sm p-6 lg:p-8 transition hover:shadow-lg hover:-translate-y-0.5"
      style={{ display: "block" }}
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: GOLD }}>
          <Icon className="w-5 h-5" style={{ color: NAVY }} />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>{demo.eyebrow}</div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 mt-0.5 inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Live
          </div>
        </div>
      </div>

      <h3 className="mt-5 font-display text-xl lg:text-2xl font-semibold leading-snug" style={{ color: NAVY }}>{demo.title}</h3>
      <p className="mt-3 text-[13px] leading-relaxed text-slate-600">{demo.body}</p>

      <ul className="mt-5 space-y-1.5">
        {demo.highlights.map((h) => (
          <li key={h} className="flex items-start gap-2 text-[12px] text-slate-700">
            <Sparkles className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: GOLD }} />
            <span>{h}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {demo.duration}</span>
          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {demo.audience}</span>
        </div>
        <span className="inline-flex items-center gap-1.5 font-semibold text-[12px] transition group-hover:gap-2.5" style={{ color: NAVY }}>
          Open demo <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
}

function PlannedCard({ title, body }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-slate-200 p-6 lg:p-8" data-testid="demo-planned">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-slate-100">
        <Zap className="w-5 h-5 text-slate-400" />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-4">Planned</div>
      <h3 className="mt-1 font-display text-xl font-semibold" style={{ color: NAVY }}>{title}</h3>
      <p className="mt-3 text-[13px] leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}
